<script setup>
import { computed, ref } from 'vue'
import { Head, useForm, usePage } from '@inertiajs/vue3'
import PaintingLayout from '@/Layouts/PaintingLayout.vue'
import BrandHero from '@/Components/Public/BrandHero.vue'
import LuxButton from '@/Components/Public/LuxButton.vue'
import { platformLabel } from '@/lib/platformLabel'

defineOptions({ layout: PaintingLayout })

const props = defineProps({
  content:      { type: Object, required: true },
  sent:         { type: Boolean, default: false },
  chat_intents: { type: Object, default: () => ({}) },
})
const c = props.content
const page = usePage()
const base = computed(() => page.props.base || '')


const MAX_PHOTOS = 10

const form = useForm({
  name: '', contact: '', venue_type: 'other', location: '', message: '',
  wall_photos: [], website: '',
})
// See InvestLayout note: useForm().post keeps preserveState, so the success
// panel must track the reactive `sent` prop via computed, not a snapshot ref.
const showForm = computed(() => !props.sent)

const photoNames = ref([])
function onFiles(e) {
  const files = Array.from(e.target.files || []).slice(0, MAX_PHOTOS)
  form.wall_photos = files
  photoNames.value = files.map(f => f.name)
}

function submit() {
  // A File array in the payload makes Inertia serialize as multipart FormData
  // (painting_inquiry[wall_photos][]), which Rails strong-params reads via
  // `wall_photos: []`.
  form.transform(d => ({
    painting_inquiry: {
      name: d.name, contact: d.contact, venue_type: d.venue_type,
      location: d.location, message: d.message, wall_photos: d.wall_photos,
    },
    website: d.website,
  })).post(`${base.value}/inquiries`, { preserveScroll: true, forceFormData: true })
}
</script>

<template>
  <Head :title="c.title" />

  <BrandHero kind="painting" :eyebrow="c.hero.eyebrow" :title="c.hero.headline">
    <p class="pt-hero__sub">{{ c.hero.sub }}</p>
    <div class="pt-hero__ctas"><LuxButton href="#enquire" variant="solid" tone="dark" glyph="arrow">{{ c.form.heading }}</LuxButton></div>
  </BrandHero>

  <section class="pt-body">
    <!-- Portfolio — clean empty state until real photos are supplied. When
         photos arrive, swap this panel for a .pt-gallery tile grid. -->
    <div class="pt-block">
      <h2 class="pt-heading">{{ c.portfolio.heading }}</h2>
      <p class="pt-prose">{{ c.portfolio.sub }}</p>
      <div class="pt-portfolio-empty">
        <svg class="pt-portfolio-empty__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <rect x="3" y="4" width="18" height="16" rx="2"/>
          <circle cx="8.5" cy="9.5" r="1.5"/>
          <path d="M21 15l-5-5L5 20"/>
        </svg>
        <p class="pt-portfolio-empty__text">{{ c.portfolio.empty }}</p>
        <LuxButton href="#enquire" variant="line" tone="light" glyph="arrow">{{ c.form.heading }}</LuxButton>
      </div>
    </div>

    <div class="pt-block">
      <h2 class="pt-heading">{{ c.services.heading }}</h2>
      <div class="pt-services">
        <div v-for="(s, i) in c.services.items" :key="i" class="pt-card">
          <h3 class="pt-card__title">{{ s.title }}</h3>
          <p class="pt-prose">{{ s.body }}</p>
        </div>
      </div>
    </div>

    <div class="pt-block">
      <h2 class="pt-heading">{{ c.process.heading }}</h2>
      <ol class="pt-steps">
        <li v-for="(s, i) in c.process.steps" :key="i" class="pt-step">
          <span class="pt-step__n fw-value">{{ i + 1 }}</span>
          <div>
            <h3 class="pt-step__title">{{ s.title }}</h3>
            <p class="pt-prose">{{ s.body }}</p>
          </div>
        </li>
      </ol>
    </div>

    <div class="pt-card pt-card--pricing">
      <h2 class="pt-heading">{{ c.pricing.heading }}</h2>
      <p class="pt-price-badge fw-value">{{ c.pricing.from_note }}</p>
      <p class="pt-prose">{{ c.pricing.body }}</p>
    </div>

    <div class="pt-card">
      <h2 class="pt-heading">{{ c.faq.heading }}</h2>
      <details v-for="(f, i) in c.faq.items" :key="i" class="pt-faq">
        <summary class="pt-faq__q">{{ f.q }}</summary>
        <p class="pt-faq__a pt-prose">{{ f.a }}</p>
      </details>
    </div>

    <div id="enquire" class="pt-card pt-card--form">
      <template v-if="sent && !showForm">
        <h2 class="pt-heading">{{ c.form.success.title }}</h2>
        <p class="pt-prose">{{ c.form.success.body }}</p>
      </template>
      <template v-else>
        <h2 class="pt-heading">{{ c.form.heading }}</h2>
        <p class="pt-prose">{{ c.form.sub }}</p>
        <form class="pt-form" @submit.prevent="submit">
          <label class="pt-field">
            <span>{{ c.form.name }}</span>
            <input v-model="form.name" type="text" required maxlength="80">
          </label>
          <label class="pt-field">
            <span>{{ c.form.contact }}</span>
            <input v-model="form.contact" type="text" required maxlength="160">
          </label>
          <div class="pt-form__row">
            <label class="pt-field">
              <span>{{ c.form.venue }}</span>
              <select v-model="form.venue_type">
                <option v-for="(label, key) in c.form.venue_options" :key="key" :value="key">{{ label }}</option>
              </select>
            </label>
            <label class="pt-field">
              <span>{{ c.form.location }}</span>
              <input v-model="form.location" type="text" maxlength="160">
            </label>
          </div>
          <label class="pt-field">
            <span>{{ c.form.photos }}</span>
            <input type="file" accept="image/jpeg,image/png,image/webp" multiple @change="onFiles">
            <small class="pt-hint">{{ c.form.photos_hint }}</small>
            <small v-if="photoNames.length" class="pt-hint">{{ photoNames.join(', ') }}</small>
          </label>
          <label class="pt-field">
            <span>{{ c.form.message }}</span>
            <textarea v-model="form.message" rows="3" maxlength="2000"></textarea>
          </label>
          <input v-model="form.website" type="text" name="website" tabindex="-1" autocomplete="off" class="pt-honeypot" aria-hidden="true">
          <LuxButton type="submit" variant="solid" tone="light" glyph="arrow" :disabled="form.processing">{{ c.form.submit }}</LuxButton>
        </form>
      </template>
    </div>

    <div v-if="Object.keys(chat_intents).length" class="pt-block pt-chat">
      <h2 class="pt-heading">{{ c.chat.heading }}</h2>
      <p class="pt-prose">{{ c.chat.sub }}</p>
      <div class="pt-chat__btns">
        <LuxButton v-for="(url, platform, i) in chat_intents" :key="platform" :href="url" :variant="i === 0 ? 'solid' : 'line'" tone="light" :glyph="platform" target="_blank" rel="noopener">
          {{ platformLabel(platform) }}
        </LuxButton>
      </div>
    </div>

    <div class="pt-card pt-card--trust">
      <h2 class="pt-heading">{{ c.trust.heading }}</h2>
      <p class="pt-prose">{{ c.trust.body }}</p>
    </div>
  </section>
</template>

<style scoped>
/* Wall-art studio — keeps its street-art hero, joins the marketing type system below. */
.pt-hero__sub { margin: 0.9rem 0 0; font-size: clamp(1.05rem, 2vw, 1.25rem); line-height: 1.5; max-width: 54ch; opacity: 0.94; }
.pt-hero__ctas { margin-top: 1.5rem; }
/* min-width: 0 on the grid children so nothing inside (a wide button, a long word) can push the page past the phone screen. */
.pt-body > * { min-width: 0; }
.pt-body { max-width: 64rem; margin: 0 auto; padding: clamp(2.5rem, 5vw, 4rem) clamp(1.25rem, 4vw, 2rem) 4rem; display: grid; gap: clamp(2.5rem, 5vw, 3.5rem); }
.pt-heading { margin: 0 0 1rem; font-family: var(--display); font-weight: 500; font-size: clamp(1.7rem, 2.8vw, 2.3rem); line-height: 1.08; letter-spacing: -0.005em; color: var(--ink); text-wrap: balance; }
.pt-heading::before { content: ""; display: block; width: 2rem; height: 1px; background: var(--gold); margin-bottom: 0.85rem; }
.pt-prose { margin: 0 0 0.75rem; color: var(--inkSoft); line-height: 1.6; }
.pt-gallery { display: grid; gap: 0.75rem; grid-template-columns: repeat(auto-fit, minmax(10rem, 1fr)); }
.pt-tile { aspect-ratio: 4 / 3; border-radius: 1rem; border: 1px solid var(--line); object-fit: cover; width: 100%; }
.pt-portfolio-empty {
  display: flex; flex-direction: column; align-items: center; text-align: center; gap: 0.85rem;
  padding: 2.75rem 1.5rem; border: 1px dashed color-mix(in oklch, var(--gold) 55%, var(--line)); border-radius: 1.25rem;
  background: linear-gradient(170deg, var(--surface) 0%, var(--sky, var(--surfaceAlt)) 100%);
}
.pt-portfolio-empty__icon { width: 2.5rem; height: 2.5rem; color: var(--muted); opacity: 0.7; }
.pt-portfolio-empty__text { margin: 0; max-width: 30rem; color: var(--inkSoft); }
.pt-services { display: grid; gap: 1rem; grid-template-columns: repeat(auto-fit, minmax(15rem, 1fr)); }
.pt-card { background: var(--surface); border: 1px solid color-mix(in oklch, var(--gold) 40%, var(--line)); border-radius: 1.25rem; padding: 1.5rem; }
.pt-card__title { margin: 0 0 0.5rem; font-family: var(--display); font-weight: 500; font-size: 1.5rem; line-height: 1.1; color: var(--ink); }
.pt-steps { list-style: none; margin: 0; padding: 0; }
.pt-step { display: grid; grid-template-columns: 3rem 1fr; gap: 1rem; padding: 1rem 0; border-top: 1px solid color-mix(in oklch, var(--gold) 35%, var(--line)); }
.pt-step:last-child { border-bottom: 1px solid color-mix(in oklch, var(--gold) 35%, var(--line)); }
.pt-step__n { font-family: var(--display); font-size: 1.5rem; line-height: 1; color: var(--gold); padding-top: 0.15rem; }
.pt-step__title { margin: 0 0 0.25rem; font-family: var(--display); font-weight: 500; font-size: 1.45rem; line-height: 1.1; color: var(--ink); }
.pt-card--pricing { text-align: left; }
.pt-price-badge { display: inline-block; margin: 0 0 0.75rem; padding: 0.45rem 1rem; border-radius: 999px; border: 1px solid color-mix(in oklch, var(--gold) 70%, var(--line)); color: var(--ink); font-family: var(--display); font-size: 1.15rem; }
.pt-faq { border-top: 1px solid var(--line); padding: 0.75rem 0; }
.pt-faq:first-of-type { border-top: none; }
.pt-faq__q { cursor: pointer; font-weight: 600; color: var(--ink); }
.pt-faq__a { margin-top: 0.5rem; }
.pt-form { display: grid; gap: 1rem; margin-top: 1rem; }
.pt-form__row { display: grid; gap: 1rem; grid-template-columns: repeat(auto-fit, minmax(14rem, 1fr)); }
.pt-field { display: grid; gap: 0.4rem; }
.pt-field > span { font-size: 0.68rem; letter-spacing: var(--eyebrow-tracking, 0.18em); text-transform: uppercase; font-weight: 600; color: var(--inkSoft); }
.pt-field input, .pt-field select, .pt-field textarea {
  padding: 0.75rem 0.9rem; border: 1px solid var(--line); border-radius: 0.75rem;
  background: var(--bg); color: var(--ink); font: inherit;
}
.pt-field input:focus, .pt-field select:focus, .pt-field textarea:focus { outline: none; border-color: var(--gold); box-shadow: 0 0 0 3px color-mix(in oklch, var(--gold) 20%, transparent); }
.pt-hint { color: var(--muted); font-size: 0.8rem; }
.pt-honeypot { position: absolute; left: -9999px; }
.pt-chat__btns { display: flex; flex-wrap: wrap; gap: 0.75rem; }
/* Form controls take the column width, never their intrinsic width — an <input>
   at its default size was the last thing widening the page on a 390px phone. */
.pt-form > *, .pt-form__row > * { min-width: 0; }
.pt-field input, .pt-field select, .pt-field textarea { width: 100%; min-width: 0; box-sizing: border-box; }
</style>
